import { IChoiceFilters } from '@/interfaces/choice.interfaces';
import { PrimitiveType } from '@/interfaces/common.interfaces';
import { FilterBlocksEnum, IFilterGridItem } from '@/interfaces/constructor.interfaces';
import {
  FiltersItemType,
  FiltersRawItemType,
  IExtendedFilterCollectionItem,
  IFilterPrimitive,
} from '@/interfaces/filters.interfaces';
import deepCopy from '@/utils/deepCopy';
import instanceOfDateRange from '@/utils/instanceOfDateRange';
import instanceOfPrimitive from '@/utils/instanceOfPrimitive';
import instanceOfRange from '@/utils/instanceOfRange';
import instanceOfReference from '@/utils/instanceOfReference';

const formatFiltersRawItem = (
  filtersRawItem: FiltersRawItemType,
  aggregateFiltersRawItem: FiltersRawItemType,
  filtersChoice: IChoiceFilters,
  filterGridItems: Array<IFilterGridItem>,
) => {
  const filtersItem = deepCopy(filtersRawItem) as FiltersItemType;

  for (const [filterKey, filterValues] of Object.entries(filtersRawItem.data)) {
    if (!filterValues.length) {
      continue;
    }

    const filterGridItem = filterGridItems.find(item => item.key === filterKey);

    if (!filterGridItem) {
      continue;
    }

    if (instanceOfReference(filterValues[0])) {
      const filterItemValues = [...filterValues] as IExtendedFilterCollectionItem[];
      const aggregateFilterItems = (aggregateFiltersRawItem.data?.[filterKey] ||
        []) as typeof filterItemValues;

      const existingFilterItemIds = new Set(
        aggregateFilterItems.map(filter => filter.id),
      );

      filtersItem.data[filterKey] = filterItemValues.reduce<
        IExtendedFilterCollectionItem[]
      >((acc, filterItemValue) => {
        const selectedFilterItemValues = filtersChoice?.[filterKey];

        const isSelectedFilterItemValue = Array.isArray(selectedFilterItemValues)
          ? selectedFilterItemValues.includes(filterItemValue.id)
          : filterItemValue.id === selectedFilterItemValues;
        const isExistFilterItemValue = existingFilterItemIds.has(filterItemValue.id);

        return [
          ...acc,
          {
            ...filterItemValue,
            available: isSelectedFilterItemValue || isExistFilterItemValue,
          },
        ];
      }, []);
      continue;
    }

    if (
      [FilterBlocksEnum.FilterSlider, FilterBlocksEnum.FilterRangeSlider].includes(
        filterGridItem.type,
      ) &&
      instanceOfRange(filterValues)
    ) {
      const aggregateFilterRangeValues = (aggregateFiltersRawItem.data?.[filterKey] ||
        filterValues) as typeof filterValues;
      filtersItem.data[filterKey] = {
        min: filterValues[0],
        max: filterValues[1],
        value: aggregateFilterRangeValues,
      };
      continue;
    }

    if (
      [FilterBlocksEnum.FilterSlider, FilterBlocksEnum.FilterRangeSlider].includes(
        filterGridItem.type,
      ) &&
      instanceOfDateRange(filterValues)
    ) {
      const aggregateFilterRangeValues = (aggregateFiltersRawItem.data?.[filterKey] ||
        filterValues) as typeof filterValues;

      const getIsoYear = (isoDate: string) => {
        return Number(isoDate.split('-')[0]);
      };

      filtersItem.data[filterKey] = {
        min: getIsoYear(filterValues[0]),
        max: getIsoYear(filterValues[1]),
        value: [
          getIsoYear(aggregateFilterRangeValues[0]),
          getIsoYear(aggregateFilterRangeValues[1]),
        ],
      };
      continue;
    }

    if (instanceOfPrimitive(filterValues[0])) {
      const filterPrimitiveValues = [...filterValues] as PrimitiveType[];
      const aggregateFilterPrimitiveValues = (aggregateFiltersRawItem.data?.[filterKey] ||
        []) as typeof filterPrimitiveValues;

      const existingFilterPrimitiveValues = new Set(
        aggregateFilterPrimitiveValues.map(filter => filter),
      );

      filtersItem.data[filterKey] = filterPrimitiveValues.reduce<IFilterPrimitive[]>(
        (acc, filterPrimitiveValue) => {
          const selectedFilterPrimitiveValues = filtersChoice?.[filterKey];

          const isSelectedFilterPrimitiveValue = Array.isArray(
            selectedFilterPrimitiveValues,
          )
            ? selectedFilterPrimitiveValues.includes(filterPrimitiveValue)
            : filterPrimitiveValue === selectedFilterPrimitiveValues;
          const isExistFilterPrimitiveValue =
            existingFilterPrimitiveValues.has(filterPrimitiveValue);

          return [
            ...acc,
            {
              value: filterPrimitiveValue,
              available: isSelectedFilterPrimitiveValue || isExistFilterPrimitiveValue,
            },
          ];
        },
        [],
      );
      continue;
    }
  }

  return filtersItem;
};

export default formatFiltersRawItem;
